// src/messaging/Composer.jsx
// 入力欄【2026-10-16】
//   ・Enter は改行、Ctrl+Enter（Mac は ⌘+Enter）か送信ボタンで送る
//   ・画像・PDF を1つ付けられる（画像は長い辺 2048px を超えたら縮小。PDF は 10MB まで）
//   ・返信（引用を上に出す）・@で呼ぶ（メンバーから選ぶ）・自分のメッセージの編集
//   ・先生（先生とのやり取り）: 定型文を入れる（送らずに入力欄へ入れるだけ）
//   ・混み合っている（BUSY）ときは、数秒待って1回だけ自動で送り直す
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { msgApi } from './api';
import { prepareAttachment, fileSize } from './util';
import MsgIcon from './MsgIcon';
import Icon from '../components/Icon';

const MAX_TEXT = 4000;
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export default function Composer({
  conversationId, members, meId, allowMentions, templatesEnabled,
  replyTo, onCancelReply, editing, onCancelEdit,
  onSent, onEdited, onFailure,
}) {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [mentionIds, setMentionIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');          // 入力欄の上に出す短い知らせ（エラーなど）
  const [mq, setMq] = useState(null);            // @の候補: { query, start } / null
  const [templates, setTemplates] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const taRef = useRef(null);
  const fileRef = useRef(null);

  // 編集を始めたら本文を入れる
  useEffect(() => {
    if (editing) { setText(editing.text || ''); setFile(null); setNote(''); setTimeout(() => taRef.current && taRef.current.focus(), 0); }
  }, [editing]);
  useEffect(() => { if (replyTo && taRef.current) taRef.current.focus(); }, [replyTo]);

  // 高さを中身に合わせる（6行まで）
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
  }, [text]);

  const candidates = useMemo(() => {
    if (!mq || !allowMentions) return [];
    const q = mq.query.toLowerCase();
    return (members || []).filter(m => m.id !== meId && !m.left && (m.name || '').toLowerCase().includes(q)).slice(0, 8);
  }, [mq, members, meId, allowMentions]);

  const detectMention = (value, caret) => {
    if (!allowMentions) { setMq(null); return; }
    const before = value.slice(0, caret);
    const m = before.match(/(^|\s)@([^\s@]*)$/);
    setMq(m ? { query: m[2], start: caret - m[2].length - 1 } : null);
  };

  const pickMention = (member) => {
    const ta = taRef.current;
    const caret = ta ? ta.selectionStart : text.length;
    const start = mq ? mq.start : caret;
    const next = text.slice(0, start) + '@' + member.name + ' ' + text.slice(caret);
    setText(next);
    setMentionIds(ids => (ids.includes(member.id) ? ids : [...ids, member.id]));
    setMq(null);
    setTimeout(() => {
      if (!ta) return;
      const pos = start + member.name.length + 2;
      ta.focus(); ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const openTemplates = async () => {
    setTplOpen(o => !o);
    if (templates) return;
    const r = await msgApi.get('/teacher/templates');
    setTemplates(r.success ? r.templates : []);
  };

  const insertTemplate = (t) => {
    const ta = taRef.current;
    const caret = ta ? ta.selectionStart : text.length;
    const next = text.slice(0, caret) + t.text + text.slice(caret);
    setText(next.slice(0, MAX_TEXT));
    setTplOpen(false);
    setTimeout(() => { if (ta) { ta.focus(); const p = caret + t.text.length; ta.setSelectionRange(p, p); } }, 0);
  };

  const onPickFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setNote('');
    const p = await prepareAttachment(f);
    if (p.error) { setNote(p.error); return; }
    setFile(p.file);
  };

  const reset = () => {
    setText(''); setFile(null); setMentionIds([]); setMq(null);
    onCancelReply && onCancelReply();
  };

  const submit = async () => {
    if (busy) return;
    const body = text.trim();
    if (editing) {
      if (!body && editing.kind === 'text') { setNote('本文を入れてください（消したいときは「送信を取り消す」）'); return; }
      setBusy(true); setNote('');
      const r = await msgApi.patch(`/messages/${editing.id}`, { text: body });
      setBusy(false);
      if (!r.success) { setNote(r.message || '直せませんでした'); onFailure && onFailure(r); return; }
      setText('');
      onEdited && onEdited(r.message);
      return;
    }
    if (!body && !file) return;
    if (body.length > MAX_TEXT) { setNote(`本文は${MAX_TEXT}文字までです`); return; }
    // 本文から消された @ は送らない
    const ids = allowMentions ? mentionIds.filter(id => {
      const m = (members || []).find(x => x.id === id);
      return m && body.includes('@' + m.name);
    }) : [];
    const fields = { text: body || undefined, reply_to_id: replyTo ? replyTo.id : undefined };
    if (ids.length) fields.mention_user_ids = ids;
    const path = `/conversations/${conversationId}/messages`;
    const once = () => (file ? msgApi.upload(path, file, fields) : msgApi.post(path, fields));

    setBusy(true); setNote('');
    let r = await once();
    if (!r.success && r.code === 'BUSY') {
      // 混み合っている: Retry-After（サーバーは3秒）待って1回だけ送り直す
      const wait = Math.min(Math.max(Number(r.retry_after) || 3, 1), 30);
      setNote(`混み合っています。${wait}秒後にもう一度送ります…`);
      await sleep(wait * 1000);
      r = await once();
    }
    setBusy(false);
    if (!r.success) {
      setNote(r.code === 'TEACHER_CLOSED' ? '' : (r.message || '送れませんでした'));
      if (['IMAGE_TOO_LARGE', 'USER_IMAGE_QUOTA', 'STORAGE_LOW', 'IMAGE_QUOTA_FULL', 'BAD_UPLOAD'].includes(r.code)) {
        // 添付だけ外して、文字は残す（文字だけなら送れることが多い）
        setFile(null);
      }
      onFailure && onFailure(r);
      return;
    }
    reset();
    onSent && onSent(r.message, r.off_hours || null);
  };

  const onKeyDown = (e) => {
    if (mq && candidates.length && (e.key === 'Enter' || e.key === 'Tab') && !e.ctrlKey && !e.metaKey) {
      e.preventDefault(); pickMention(candidates[0]); return;
    }
    if (e.key === 'Escape') {
      if (mq) { setMq(null); return; }
      if (editing) { onCancelEdit(); setText(''); return; }
      if (replyTo) { onCancelReply(); return; }
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
  };

  const canSend = !busy && (text.trim().length > 0 || (!!file && !editing));

  return (
    <div className="msg-composer">
      {note && <div className="msg-composer-note" role="alert">{note}</div>}
      {editing && (
        <div className="msg-composer-ctx">
          <span><strong>編集中</strong>（送ってから24時間まで直せます）</span>
          <button type="button" className="btn-link" onClick={() => { onCancelEdit(); setText(''); setNote(''); }}>やめる</button>
        </div>
      )}
      {!editing && replyTo && (
        <div className="msg-composer-ctx">
          <span className="msg-composer-quote">
            <strong>{replyTo.sender ? replyTo.sender.name : ''}</strong>への返信：
            {replyTo.text ? replyTo.text.slice(0, 60) : replyTo.image ? '[画像]' : replyTo.file ? '[ファイル]' : ''}
          </span>
          <button type="button" className="btn-link" onClick={onCancelReply}>やめる</button>
        </div>
      )}
      {file && !editing && (
        <div className="msg-composer-ctx">
          <span>添付：{file.name}（{fileSize(file.size)}）</span>
          <button type="button" className="btn-link" onClick={() => setFile(null)}>外す</button>
        </div>
      )}
      {mq && candidates.length > 0 && (
        <ul className="msg-mention-picker" role="listbox" aria-label="呼ぶ人を選ぶ">
          {candidates.map(m => (
            <li key={m.id}><button type="button" onMouseDown={(e) => { e.preventDefault(); pickMention(m); }}>@{m.name}</button></li>
          ))}
        </ul>
      )}
      {tplOpen && (
        <div className="msg-template-picker">
          {templates === null ? <p className="msg-help">読み込み中...</p>
            : templates.length === 0 ? <p className="msg-help">定型文はまだありません。メッセージの受付設定の画面で作れます。</p>
              : templates.map(t => (
                <button type="button" key={t.id} onClick={() => insertTemplate(t)}>
                  <strong>{t.title}</strong><span>{t.text.slice(0, 60)}</span>
                </button>
              ))}
        </div>
      )}
      <div className="msg-composer-row">
        {!editing && (
          <>
            <button type="button" className="msg-icon-btn" onClick={() => fileRef.current && fileRef.current.click()}
                    aria-label="画像・PDF を付ける" title="画像・PDF を付ける" disabled={busy}>
              <MsgIcon name="clip" size={20} />
            </button>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={onPickFile} />
          </>
        )}
        {templatesEnabled && !editing && (
          <button type="button" className="msg-icon-btn" onClick={openTemplates} aria-label="定型文" title="定型文">
            <Icon name="list" size={20} />
          </button>
        )}
        <textarea ref={taRef} className="msg-input" rows={1} value={text} maxLength={MAX_TEXT + 100}
                  placeholder={editing ? 'メッセージを直す' : 'メッセージを入力（Ctrl+Enter で送信）'}
                  onChange={(e) => { setText(e.target.value); detectMention(e.target.value, e.target.selectionStart); }}
                  onKeyDown={onKeyDown}
                  onClick={(e) => detectMention(e.target.value, e.target.selectionStart)}
                  onBlur={() => setTimeout(() => setMq(null), 150)} />
        <button type="button" className="msg-send-btn" onClick={submit} disabled={!canSend} aria-label={editing ? '直す' : '送信'}>
          {editing ? <Icon name="check" size={20} /> : <MsgIcon name="send" size={20} />}
        </button>
      </div>
    </div>
  );
}
